import { supabase } from '../supabase';
import { normalizeEdicionEstadoPublico } from '../utils/edicionPublicVisibility';

const VIS_PUBLICO_FILTER = 'visible_publico.is.null,visible_publico.eq.true';

export type ParticipacionEdicionActiva = {
  participa: boolean;
  /** Nombre de la edición con estado `activa` en la que está inscrito (si aplica). */
  edicionNombre: string | null;
  edicionId: number | null;
  /** URL del logotema de la edición activa (columna `ediciones.logotema`), si existe. */
  edicionLogotema: string | null;
  /** Primera propuesta pública del restaurante para esa edición (misma lógica que catálogo): `edicion_id` coincidente o `null` si el local está inscrito. */
  propuestaIdVotacion: string | null;
};

async function fetchPropuestaPublicaVotacionId(
  restauranteIdNum: number,
  edicionIdNum: number
): Promise<string | null> {
  const { data: exact, error: e1 } = await supabase
    .from('propuestas')
    .select('id')
    .eq('restaurante_id', restauranteIdNum)
    .eq('edicion_id', edicionIdNum)
    .or(VIS_PUBLICO_FILTER)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!e1 && exact?.id != null) {
    return String(exact.id);
  }

  const { data: orphan, error: e2 } = await supabase
    .from('propuestas')
    .select('id')
    .eq('restaurante_id', restauranteIdNum)
    .is('edicion_id', null)
    .or(VIS_PUBLICO_FILTER)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!e2 && orphan?.id != null) {
    return String(orphan.id);
  }

  return null;
}

const vacio: ParticipacionEdicionActiva = {
  participa: false,
  edicionNombre: null,
  edicionId: null,
  edicionLogotema: null,
  propuestaIdVotacion: null,
};

/**
 * Indica si el restaurante está vinculado a una edición votable
 * (`estado = activa` o `modo_prueba`) y resuelve la propuesta para `/votar/:id`.
 */
export async function fetchParticipacionEdicionActivaRestaurante(
  restauranteId: string
): Promise<ParticipacionEdicionActiva> {
  const rid = parseInt(restauranteId, 10);
  if (Number.isNaN(rid)) {
    return { ...vacio };
  }

  const { data, error } = await supabase
    .from('restaurantes_ediciones')
    .select(`
      edicion_id,
      ediciones ( id, nombre, estado, logotema, modo_prueba )
    `)
    .eq('restaurante_id', rid);

  if (error || !data?.length) {
    return { ...vacio };
  }

  type EdicionRow = {
    id?: unknown;
    nombre?: string | null;
    estado?: string | null;
    logotema?: string | null;
    modo_prueba?: boolean | null;
  };

  const buildResult = async (ed: EdicionRow): Promise<ParticipacionEdicionActiva> => {
    const edicionIdNum = Number(ed.id);
    if (Number.isNaN(edicionIdNum)) {
      return { ...vacio };
    }
    const propuestaIdVotacion = await fetchPropuestaPublicaVotacionId(rid, edicionIdNum);
    const rawLogo =
      ed.logotema != null && String(ed.logotema).trim() !== ''
        ? String(ed.logotema).trim()
        : null;
    return {
      participa: true,
      edicionNombre: typeof ed.nombre === 'string' && ed.nombre.trim() ? ed.nombre.trim() : null,
      edicionId: edicionIdNum,
      edicionLogotema: rawLogo,
      propuestaIdVotacion,
    };
  };

  let candidataPrueba: EdicionRow | null = null;

  for (const row of data as { ediciones?: EdicionRow | null }[]) {
    const ed = row.ediciones;
    if (!ed) continue;
    if (normalizeEdicionEstadoPublico(ed.estado) === 'activa') {
      return buildResult(ed);
    }
    if (ed.modo_prueba === true && !candidataPrueba) {
      candidataPrueba = ed;
    }
  }

  if (candidataPrueba) {
    return buildResult(candidataPrueba);
  }

  return { ...vacio };
}
